import {
  Avatar,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LanguageIcon from '@mui/icons-material/Language';
import CloseIcon from '@mui/icons-material/Close';

import type { Lesson } from "../../api/course-service";
import { lessonTextPath } from "../../api/course-service";
import type { ProgressLesson } from "../../api/user-progress-service";
import { passedSessions } from "../../utils/course-progress";
import Header from "../text/Header";

import { LessonVerifyButton } from "./LessonVerifyButton";

export type LessonDetailsProps = {
    courseId: string;
    lesson: Lesson;
    progress: ProgressLesson;
    caption?: string;
    verifyLoading: boolean;
    onVerify: (session: number) => void;
    onClose: () => void;
};

/** Everything about the chosen lesson: what it covers, where to read about it, and its verification. */
export function LessonDetails({ courseId, lesson, progress, caption, verifyLoading, onVerify, onClose }: LessonDetailsProps) {
    return (
        <Stack spacing={ 2 } sx={ { p: 3 } }>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={ 2 }>
                <Stack spacing={ 0.5 }>
                    <Header text={ `${ lesson.group } - ${ lesson.title }` }/>
                    { caption && <Typography variant="body2" sx={ { color: 'text.secondary' } }>{ caption }</Typography> }
                </Stack>
                <IconButton aria-label="Close lesson" onClick={ onClose }><CloseIcon/></IconButton>
            </Stack>

            <Typography>{ lesson.description || lesson.short }</Typography>

            { lesson.hasText && (
                <Link component={ RouterLink } to={ lessonTextPath(courseId, lesson.id) }>Read more</Link>
            ) }

            { !!lesson.covers.length && (<>
                <Divider>Covers</Divider>
                <List dense>
                    { lesson.covers.map((item) => (
                        <ListItem key={ item }><ListItemText primary={ item }/></ListItem>
                    )) }
                </List>
            </>) }

            <Divider>Materials</Divider>
            <List>
                { lesson.references.map((reference) => (
                    <ListItem key={ reference.url }>
                        <ListItemAvatar><Avatar><LanguageIcon/></Avatar></ListItemAvatar>
                        <ListItemText primary={ <Link href={ reference.url } target="_blank" rel="noopener">{ reference.title }</Link> }/>
                    </ListItem>
                )) }
            </List>

            <Divider>Progress</Divider>
            { lesson.practicable ? (<>
                <LessonVerifyButton status={ progress.status } recapDate={ progress.recapDate } loading={ verifyLoading } onStart={ onVerify }/>
                <Stepper activeStep={ passedSessions(progress.status) } orientation="vertical">
                    <Step><StepLabel>Learn and verify</StepLabel></Step>
                    <Step><StepLabel>Recap after 1 week, and the lesson is finished</StepLabel></Step>
                </Stepper>
            </>) : (
                <Typography variant="body2">This lesson does not carry enough questions to be verified yet.</Typography>
            ) }
        </Stack>
    );
}
